import { injectable } from 'inversify';

import {
  DeadlineSettings,
  EventTypeWorkflowNode,
  EventTypeWorkflowNodeAction,
  GetEventTypeResponse,
  WorkflowNodeActionAttribute,
} from '@app/gen/settings';

import {
  CreateEventTypeDto,
  FlowEdgeDto,
  NodeActionAttributeDto,
  NodeActionDto,
} from '../../dto';
import { START_EVENT_NODE_ID } from '../../constants/eventTypes';
import { DeadlineDto } from '../../dto/create-event-type';
import { ExecutorFieldsDto } from '../../dto/create-event-type/executor-fields.dto';
import { FlowNodeType } from '../../types';

interface WorkflowTransitionWithResolvedIds {
  actionType: string;
  sourceId: string;
  targetId: string;
}

/**
 * Маппер данных страницы редактирования типа заявки.
 */
@injectable()
export class EventTypeEditMapper {
  /**
   * Преобразует ответ бэка в модель формы.
   *
   * @param response - Ответ бэка.
   * @param companyId - Идентификатор компании из query-параметров.
   * @returns Начальные значения формы.
   */
  public toFormModel(
    response: GetEventTypeResponse,
    companyId: string,
  ): CreateEventTypeDto {
    const { event_type: eventType } = response;

    const workflowNodes = response.workflow.nodes ?? [];

    const primaryActionByNodeId = workflowNodes.reduce<
      Record<string, EventTypeWorkflowNodeAction | undefined>
    >((acc, node) => {
      if (!node.id) {
        return acc;
      }

      acc[node.key ?? node.id] = node.actions?.find(
        ({ is_primary }) => is_primary,
      );

      return acc;
    }, {});
    const nodeIdMap = this.getNodeIdMap(workflowNodes);
    const transitions = this.getResolvedTransitions(
      response.workflow,
      nodeIdMap,
    );
    const outgoingTransitionsMap = transitions.reduce<
      Record<string, WorkflowTransitionWithResolvedIds[]>
    >((acc, transition) => {
      if (transition.actionType === 'return') {
        return acc;
      }

      acc[transition.sourceId] = [
        ...(acc[transition.sourceId] ?? []),
        transition,
      ];
      return acc;
    }, {});

    const targetIds = new Set(transitions.map(({ targetId }) => targetId));
    const sourceIds = new Set(transitions.map(({ sourceId }) => sourceId));
    const startNode = workflowNodes.find((node) => {
      if (!node.id) return false;
      const resolvedId = node.key ?? node.id;
      return sourceIds.has(resolvedId) && !targetIds.has(resolvedId);
    });
    const startNodeId = startNode ? (startNode.key ?? startNode.id) : undefined;

    const systemNodeIds = new Set(
      Object.entries(primaryActionByNodeId)
        .filter(([, action]) => action?.type?.startsWith('system_'))
        .map(([id]) => id),
    );
    const collapsedTransitions = this.removeLinearNodes(
      transitions,
      systemNodeIds,
    );

    const nodes = workflowNodes.reduce<CreateEventTypeDto['nodes']>(
      (acc, node) => {
        if (!node.id) {
          return acc;
        }

        const nodeId = node.key ?? node.id;
        if (systemNodeIds.has(nodeId)) {
          return acc;
        }
        const isStart = nodeId === startNodeId;
        const isFinish = (outgoingTransitionsMap[nodeId] ?? []).length === 0;

        if (isStart) {
          const startPrimaryAction = node.actions?.find(
            ({ is_primary }) => is_primary,
          );
          const {
            executor: startExecutor,
            companyGroup: startCompanyGroup,
            companyGroupCustomName: startCompanyGroupCustomName,
            organizationType: startOrganizationType,
            hierarchyLevel: startHierarchyLevel,
            assistantHierarchyLevel: startAssistantHierarchyLevel,
          } = this.mapWorkflowExecutor(startPrimaryAction);

          acc[START_EVENT_NODE_ID] = {
            type: 'StartEvent',
            stageName: node.name,
            createForOthers: eventType.create_for_others,
            batchEnabled: eventType.batch_enabled,
            noEmployee: eventType.no_employee,
            executor: startExecutor,
            ...(startCompanyGroup?.length
              ? { companyGroup: startCompanyGroup }
              : {}),
            ...(startCompanyGroupCustomName
              ? { companyGroupCustomName: startCompanyGroupCustomName }
              : {}),
            ...(startOrganizationType
              ? { organizationType: startOrganizationType }
              : {}),
            ...(startHierarchyLevel !== undefined
              ? { hierarchyLevel: startHierarchyLevel }
              : {}),
            ...(startAssistantHierarchyLevel !== undefined
              ? { assistantHierarchyLevel: startAssistantHierarchyLevel }
              : {}),
            notifyIntegration: node.notify_integration ?? false,
          };
        } else if (isFinish) {
          acc[nodeId] = {
            type: 'FinishEvent',
            actions: this.mapFinishNodeActions(node.actions),
            notifyIntegration: node.notify_integration,
          };
        } else {
          acc[nodeId] = this.mapWorkflowNodeToFormNodeData(
            node,
            outgoingTransitionsMap[nodeId] ?? [],
          );
        }

        return acc;
      },
      {},
    );

    const { branchGroupIds, parallelGroups } = this.getParallelGroups(
      workflowNodes,
      outgoingTransitionsMap,
    );

    branchGroupIds.forEach((groupId, nodeId) => {
      const nodeData = nodes[nodeId];

      if (nodeData && nodeData.type === 'UserTask') {
        nodeData.parallelGroupId = groupId;
      }
    });

    parallelGroups.forEach(({ joinAny }, groupId) => {
      if (!nodes[groupId]) {
        nodes[groupId] = { type: 'ParallelGroup', joinAny };
      }
    });

    const edges = collapsedTransitions
      .filter(({ actionType }) => actionType !== 'return')
      .map<FlowEdgeDto>((transition, index) => {
        const sourceId =
          transition.sourceId === startNodeId
            ? START_EVENT_NODE_ID
            : transition.sourceId;
        const targetId =
          transition.targetId === startNodeId
            ? START_EVENT_NODE_ID
            : transition.targetId;
        const sourceNode = nodes[sourceId];
        const primaryActionType =
          primaryActionByNodeId[transition.sourceId]?.type;

        let condition: FlowEdgeDto['condition'];

        if (sourceNode?.type === 'BranchTask') {
          condition =
            transition.actionType === primaryActionType ? 'main' : 'secondary';
        }

        return {
          id: `flow-${index}`,
          sourceId,
          targetId,
          ...(condition ? { condition } : {}),
        };
      });

    const returns = collapsedTransitions
      .filter(({ actionType }) => actionType === 'return')
      .map<FlowEdgeDto>((transition, index) => ({
        id: `return-${index}`,
        sourceId:
          transition.sourceId === startNodeId
            ? START_EVENT_NODE_ID
            : transition.sourceId,
        targetId:
          transition.targetId === startNodeId
            ? START_EVENT_NODE_ID
            : transition.targetId,
      }));

    return {
      companyId,
      eventType: eventType.name,
      groupTypes: eventType.event_type_group ?? eventType.name,
      customType: eventType.custom_type,
      nodes,
      edges,
      returns,
    };
  }

  private removeLinearNodes(
    transitions: WorkflowTransitionWithResolvedIds[],
    nodeIds: Set<string>,
  ): WorkflowTransitionWithResolvedIds[] {
    let result = transitions;

    for (const id of nodeIds) {
      const incoming = result.filter((t) => t.targetId === id);
      const outgoing = result.filter((t) => t.sourceId === id);

      result = result
        .filter((t) => t.sourceId !== id && t.targetId !== id)
        .concat(
          incoming.flatMap((i) =>
            outgoing.map((o) => ({ ...i, targetId: o.targetId })),
          ),
        );
    }

    return result;
  }

  private getNodeIdMap(nodes: EventTypeWorkflowNode[]): Record<string, string> {
    return nodes.reduce<Record<string, string>>((acc, node) => {
      if (!node.id) return acc;

      const resolvedId = node.key ?? node.id;

      acc[node.id] = resolvedId;

      if (node.key) {
        acc[node.key] = resolvedId;
      }

      return acc;
    }, {});
  }

  private getResolvedTransitions(
    workflow: GetEventTypeResponse['workflow'],
    nodeIdMap: Record<string, string>,
  ): WorkflowTransitionWithResolvedIds[] {
    const transitions = workflow.transitions ?? [];

    return transitions.map((transition) => ({
      actionType: transition.action_type,
      sourceId: nodeIdMap[transition.from_node] ?? transition.from_node,
      targetId: nodeIdMap[transition.to_node] ?? transition.to_node,
    }));
  }

  private getParallelGroups(
    nodes: EventTypeWorkflowNode[],
    outgoingTransitionsMap: Record<string, WorkflowTransitionWithResolvedIds[]>,
  ) {
    const branchGroupIds = new Map<string, string>();
    const parallelGroups = new Map<string, { joinAny: 'true' | 'false' }>();

    const getNodeById = (id: string) =>
      nodes.find((n) => (n.key ?? n.id) === id);

    for (const node of nodes) {
      if (!node.id) continue;

      const sourceId = node.key ?? node.id;
      const outgoing = outgoingTransitionsMap[sourceId];
      if (!outgoing || outgoing.length <= 1) continue;

      const actionTypes = outgoing.map(({ actionType }) => actionType);
      const duplicatedActionType = actionTypes.find(
        (type, _, arr) => arr.indexOf(type) !== arr.lastIndexOf(type),
      );
      if (!duplicatedActionType) continue;

      const group = outgoing.filter(
        (t) => t.actionType === duplicatedActionType,
      );
      if (new Set(group.map((t) => t.targetId)).size !== group.length) continue;

      const groupId = `parallel-group-${sourceId}`;
      const branchIds = group.map(({ targetId }) => targetId);

      const branchOutgoing = branchIds.map(
        (id) => outgoingTransitionsMap[id] ?? [],
      );
      const firstTargets = branchOutgoing[0].map((t) => t.targetId);
      const joinNodeId = firstTargets.find((targetId) =>
        branchOutgoing.every((outgoings) =>
          outgoings.some((t) => t.targetId === targetId),
        ),
      );
      const joinAny =
        joinNodeId && getNodeById(joinNodeId)?.join_any ? 'true' : 'false';

      parallelGroups.set(groupId, { joinAny });

      for (const { targetId } of group) {
        if (!branchGroupIds.has(targetId))
          branchGroupIds.set(targetId, groupId);
      }
    }

    return { branchGroupIds, parallelGroups };
  }

  private mapWorkflowNodeToFormNodeData(
    node: EventTypeWorkflowNode,
    outgoingTransitions: WorkflowTransitionWithResolvedIds[],
  ): CreateEventTypeDto['nodes'][string] {
    const primaryAction = node.actions?.find(({ is_primary }) => is_primary);
    const {
      executor,
      companyGroup,
      companyGroupCustomName,
      organizationType,
      hierarchyLevel,
      assistantHierarchyLevel,
    } = this.mapWorkflowExecutor(primaryAction);

    return {
      type: this.mapApiNodeTypeToFlowNodeType(node, outgoingTransitions),
      stageName: node.name,
      executor,
      ...(companyGroup?.length ? { companyGroup } : {}),
      ...(companyGroupCustomName ? { companyGroupCustomName } : {}),
      ...(organizationType ? { organizationType } : {}),
      ...(hierarchyLevel !== undefined ? { hierarchyLevel } : {}),
      ...(assistantHierarchyLevel !== undefined
        ? { assistantHierarchyLevel }
        : {}),
      ...this.mapWorkflowActions(node.actions),
      ...this.mapWorkflowDeadlines(node),
      companyCancelable: node.company_cancelable,
      employeeCancelable: node.employee_cancelable,
      notifyIntegration: node.notify_integration,
      stageDescription: node.description,
    };
  }

  private mapAttribute(
    attribute: WorkflowNodeActionAttribute,
  ): NodeActionAttributeDto {
    const mappedAttribute: NodeActionAttributeDto = {
      id: attribute.id,
      name: attribute.name,
      type: attribute.type,
      required: attribute.required,
      isMultiple: attribute.is_multiple ?? false,
      ...(attribute.description ? { description: attribute.description } : {}),
    };

    if (attribute.options?.choices) {
      mappedAttribute.choices = attribute.options.choices;
    }

    if (attribute.options?.catalog_name) {
      mappedAttribute.catalogName = attribute.options.catalog_name;
    }

    return mappedAttribute;
  }

  private mapWorkflowActions(
    actions: EventTypeWorkflowNodeAction[] | undefined,
  ): { actions: NodeActionDto[] } {
    return {
      actions: (actions ?? []).map<NodeActionDto>((action) => ({
        type: action.type,
        isPrimary: action.is_primary,
        name: action.name,
        documentName: action.document_type_name,
        roles: action.roles
          ?.map(({ name }) => name)
          .filter((name): name is string => typeof name === 'string'),
        attributes: action.attributes?.map((attribute) =>
          this.mapAttribute(attribute),
        ),
        ...(action.type === 'sign'
          ? { signType: action.signature_type ?? 'pep' }
          : {}),
      })),
    };
  }

  private mapFinishNodeActions(
    actions: EventTypeWorkflowNodeAction[] | undefined,
  ): NodeActionDto[] {
    const mappedActions = this.mapWorkflowActions(actions).actions;

    if (!mappedActions.length) {
      return [{ type: 'accept', isPrimary: true }];
    }

    const primaryAction =
      mappedActions.find((action) => action.isPrimary) ?? mappedActions[0];
    const finishActionType = this.mapFinishActionType(primaryAction);

    return [{ ...primaryAction, type: finishActionType, isPrimary: true }];
  }

  private mapFinishActionType(
    action?: NodeActionDto,
  ): Extract<NodeActionDto['type'], 'accept' | 'decline'> {
    if (action?.type === 'accept' || action?.type === 'decline') {
      return action.type;
    }

    if (action?.name === 'Отклонено') {
      return 'decline';
    }

    return 'accept';
  }

  private mapWorkflowExecutor(primaryAction?: EventTypeWorkflowNodeAction): {
    executor: ExecutorFieldsDto['executor'];
    companyGroup?: ExecutorFieldsDto['companyGroup'];
    companyGroupCustomName?: ExecutorFieldsDto['companyGroupCustomName'];
    organizationType?: ExecutorFieldsDto['organizationType'];
    hierarchyLevel?: ExecutorFieldsDto['hierarchyLevel'];
    assistantHierarchyLevel?: ExecutorFieldsDto['assistantHierarchyLevel'];
  } {
    const roles = primaryAction?.roles ?? [];
    const normalizedRoles = roles.filter(
      (
        role,
      ): role is Exclude<
        EventTypeWorkflowNodeAction['roles'],
        undefined
      >[number] =>
        typeof role === 'object' &&
        role !== null &&
        typeof role.type === 'string',
    );
    const executor = [...new Set(normalizedRoles.map(({ type }) => type))];

    if (primaryAction?.is_employee && !executor.includes('employee')) {
      executor.unshift('employee');
    }

    const companyGroup = normalizedRoles.reduce<string[]>((acc, role) => {
      if (role.type === 'group' && role.group?.id) {
        acc.push(role.group.id);
      }

      return acc;
    }, []);

    const groupRole = normalizedRoles.find(
      ({ type, group }) => type === 'group' && group?.id,
    );

    const group = groupRole?.group;
    const name = groupRole?.name;

    const hasCustomName = !!name && name !== group?.name;

    const companyGroupCustomName =
      group && hasCustomName ? { value: group.id, label: name } : undefined;

    const managerRole = normalizedRoles.find((role) => role.type === 'manager');
    const assistantRole = normalizedRoles.find(
      (role) => role.type === 'assistant',
    );

    return {
      executor,
      ...(companyGroup.length ? { companyGroup } : {}),
      ...(companyGroupCustomName ? { companyGroupCustomName } : {}),
      ...(managerRole?.manager_type
        ? {
            organizationType:
              managerRole.manager_type as ExecutorFieldsDto['organizationType'],
          }
        : {}),
      ...(managerRole?.manager_level !== undefined
        ? { hierarchyLevel: managerRole.manager_level }
        : {}),
      ...(assistantRole?.assistant_level !== undefined
        ? { assistantHierarchyLevel: assistantRole.assistant_level }
        : {}),
    };
  }

  private mapWorkflowDeadlines(node: EventTypeWorkflowNode): {
    hasDeadline: 'true' | 'false';
    deadline: DeadlineDto[];
    deadlineMode?: DeadlineSettings['mode'];
  } {
    const deadlineSettings = node.deadline_settings?.settings;

    const deadlineMode = node.deadline_settings?.mode || undefined;

    if (!deadlineSettings?.length) {
      return {
        hasDeadline: 'false',
        deadline: [],
        deadlineMode,
      };
    }

    return {
      hasDeadline: 'true',
      deadline: deadlineSettings.map<DeadlineDto>(
        ({
          reference,
          time_unit: unit,
          outcome,
          term,
          round_days: round,
          attribute,
        }) => ({
          reference,
          unit,
          outcome,
          term,
          round: round ? 'true' : 'false',
          ...(attribute && {
            attribute: attribute.id,
            attributeName: attribute.name,
          }),
        }),
      ),
      deadlineMode,
    };
  }

  private mapApiNodeTypeToFlowNodeType(
    node: EventTypeWorkflowNode,
    outgoingTransitions: WorkflowTransitionWithResolvedIds[],
  ): FlowNodeType {
    if (outgoingTransitions.length === 0) {
      return 'FinishEvent';
    }

    const actions = (node.actions ?? []).filter(
      ({ type }) => type !== 'return',
    );
    const primaryAction = actions.find(({ is_primary }) => is_primary);

    if (primaryAction?.type === 'system') {
      return 'SystemTask';
    }

    if (
      actions.length > 1 &&
      actions.some(({ type }) => type === 'accept') &&
      actions.some(({ type }) => type === 'decline') &&
      outgoingTransitions.length >= actions.length &&
      outgoingTransitions.every(({ actionType }) =>
        actions.some(({ type }) => type === actionType),
      ) &&
      outgoingTransitions.some(({ actionType }) => actionType === 'accept') &&
      outgoingTransitions.some(({ actionType }) => actionType === 'decline')
    ) {
      return 'BranchTask';
    }

    return 'UserTask';
  }
}
